/**
 * Media-device enumeration + camera-start helpers (SC-273).
 *
 * Browser gotcha: `navigator.mediaDevices.enumerateDevices()` only returns real, *labeled*
 * input devices after a successful `getUserMedia()` grant. Before that it yields either
 * nothing for the kind or unlabeled placeholder entries (empty `label`/`deviceId`). So a user
 * who joins with the camera OFF — we only request permission for the tracks they enabled —
 * sees an empty camera picker ("No devices found") and has to reconnect. These helpers add an
 * on-demand permission probe so the list can be populated regardless of the join-time choice.
 */

export type MediaInputKind = 'videoinput' | 'audioinput'

/** navigator.mediaDevices seams, injectable for tests. */
export interface MediaDeviceDeps {
  enumerate: () => Promise<MediaDeviceInfo[]>
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>
}

function defaultDeps(): MediaDeviceDeps {
  return {
    enumerate: () => navigator.mediaDevices.enumerateDevices(),
    getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c),
  }
}

/**
 * Whether the device list is untrustworthy and a getUserMedia permission probe is warranted:
 * there are no entries of this kind, or every entry is unlabeled (empty label or deviceId),
 * which means permission has not been granted yet.
 */
export function needsPermissionProbe(devices: MediaDeviceInfo[], kind: MediaInputKind): boolean {
  const ofKind = devices.filter((d) => d.kind === kind)
  if (ofKind.length === 0) return true
  return ofKind.every((d) => !d.label || !d.deviceId)
}

const kindConstraint = (kind: MediaInputKind): MediaStreamConstraints =>
  kind === 'videoinput' ? { video: true } : { audio: true }

/**
 * Enumerate input devices of a kind. When `allowProbe` (default) and the current list is
 * empty/unlabeled, first trigger a one-shot `getUserMedia` permission probe, stop the probe
 * stream immediately (we only want the grant so labels/deviceIds appear), then re-enumerate.
 * Pass `allowProbe: false` for passive refreshes (e.g. a `devicechange` event) so we never
 * pop a permission prompt the user did not initiate.
 */
export async function enumerateInputDevices(
  kind: MediaInputKind,
  options: { allowProbe?: boolean } = {},
  deps: MediaDeviceDeps = defaultDeps(),
): Promise<MediaDeviceInfo[]> {
  const { allowProbe = true } = options
  let all = await deps.enumerate()

  if (allowProbe && needsPermissionProbe(all, kind)) {
    try {
      const probe = await deps.getUserMedia(kindConstraint(kind))
      probe.getTracks().forEach((t) => t.stop())
      all = await deps.enumerate()
    } catch {
      // Permission denied / no device present — fall through with what we have.
    }
  }

  return all.filter((d) => d.kind === kind)
}

/**
 * Whether a camera-start failure is a transient device-acquisition hiccup worth retrying.
 * `AbortError` ("Timeout starting video source"), `NotReadableError` (device briefly held by
 * another app or a not-yet-released prior track), and `TimeoutError` are retryable.
 * `NotAllowedError` (permission denied), `OverconstrainedError`, and `NotFoundError` are NOT —
 * retrying cannot fix them.
 */
export function isRetryableCameraStartError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name
  return name === 'AbortError' || name === 'NotReadableError' || name === 'TimeoutError'
}

/**
 * Whether a camera-start failure means the user has blocked access, as opposed
 * to a transient device problem.
 *
 * `NotAllowedError` is the standard denial. `SecurityError` is what some
 * browsers raise for an insecure origin. Neither is worth retrying — the fix is
 * a browser-level permission change, so the UI should say that rather than let
 * the user keep pressing a button that cannot succeed.
 */
export function isCameraPermissionError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name
  return name === 'NotAllowedError' || name === 'SecurityError'
}

/** Human-facing noun for a device kind, for use in labels and empty states. */
export function mediaKindNoun(kind: MediaInputKind): string {
  return kind === 'videoinput' ? 'camera' : 'microphone'
}

/**
 * A meaningful menu label for one enumerated device.
 *
 * Browsers hand back placeholder entries with an empty `label` **and** empty
 * `deviceId` when permission has not been granted. Rendering those with a naive
 * `label || \`Camera ${deviceId.slice(0, 4)}\`` produces a bare "Camera " — an
 * option that looks selectable, says nothing, and does nothing. This names the
 * actual situation instead.
 *
 * Shared by the pre-join dialog and the in-call device menu so the two surfaces
 * describe the same hardware the same way.
 */
export function describeMediaDevice(
  device: Pick<MediaDeviceInfo, 'label' | 'deviceId'>,
  kind: MediaInputKind
): string {
  const label = device.label?.trim()
  if (label) return label

  const noun = mediaKindNoun(kind)
  const shortId = device.deviceId?.trim().slice(0, 5)
  if (shortId) {
    // Permission granted but the browser withheld a name — uncommon, and the id
    // at least distinguishes one device from another.
    return `Unnamed ${noun} (${shortId})`
  }

  // Neither label nor id: a permission placeholder, not a real device.
  return `Allow ${noun} access to choose a device`
}

/** Empty-state text when a kind has no devices at all. */
export function noMediaDevicesMessage(kind: MediaInputKind): string {
  return `No ${mediaKindNoun(kind)} found`
}
